import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'; // Added useEffect
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox"; // Import Checkbox
import { SimpleCalendar } from "../ui/simple-calendar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format, addYears } from 'date-fns'; // Import addYears
import { CalendarIcon, Check, AlertTriangle } from 'lucide-react';
import { LoadingSpinner } from '../ui/loading-spinner';
import { getAuth } from 'firebase/auth';
import { expenseService } from '../../services/expenseService';
// Import Firestore functions and db instance
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useToast } from "../../hooks/use-toast";
import { Alert, AlertDescription } from "../ui/alert";
import { cn } from "../../lib/utils";
import { useTheme } from '../ui/theme-provider'; // Import useTheme
import { encryptData, decryptData } from '../../encryptionUtil'; // Import encryptData and decryptData
import { Camera, Upload, X, FileText, ImageIcon, Clock } from 'lucide-react'; // Import icons for receipt upload
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import SmartReceiptScanner from './SmartReceiptScanner';
import { extractReceiptText } from '../../utils/receiptUtils';

// Helper function to get storage key (copied from DashboardPage for consistency)
const getStorageKey = (type, urgency) => `dismissed_${type}_${urgency}_alert`;

// Function to detect potential renewals
const detectRenewal = (category, description) => {
  const descLower = description?.toLowerCase() || '';
  const isGdcRelated = descLower.includes('gdc') || descLower.includes('general dental council');
  const isInsuranceRelated = descLower.includes('indemnity') || descLower.includes('insurance');
  const isRenewalKeyword = descLower.includes('renewal') || descLower.includes('registration') || descLower.includes('annual fee');

  if ((category === 'membership' || isGdcRelated) && (isRenewalKeyword || category === 'membership')) {
    return { type: 'gdc' };
  }
  if ((category === 'insurance' || isInsuranceRelated) && (isRenewalKeyword || category === 'insurance')) {
    return { type: 'insurance' };
  }
  if (category === 'other' && isRenewalKeyword) {
    if (isGdcRelated) return { type: 'gdc' };
    if (isInsuranceRelated) return { type: 'insurance' };
  }
  return null;
};


const StandardExpenseForm = ({ 
  expenseToEdit, 
  onSave,   
  onCancel, 
  onExpenseSaved, 
  categories, 
  gdcExpiryDate, 
  gdcProvider,
  insuranceExpiryDate, 
  insuranceProvider: currentInsuranceProviderProp,
  userId 
}) => {
  // Auth and toast
  const auth = getAuth();
  const { toast } = useToast();
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({}); // Add this missing state
  const [successMessage, setSuccessMessage] = useState("");
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { theme } = useTheme();  
  
  // Form data state
  const [newExpense, setNewExpense] = useState({ // Renamed from formData to newExpense and setFormData to setNewExpense
    date: new Date(),
    category: '',
    description: '',
    amount: '',
    mileage: '',
    mileageRate: '45'
  });
  
  // Renewal Prompt State
  const [showRenewalPrompt, setShowRenewalPrompt] = useState(false);
  const [potentialRenewalInfo, setPotentialRenewalInfo] = useState(null); // { type: 'gdc' | 'insurance', currentExpiry: Date | null, suggestedExpiry: Date | null }
  const [confirmProfileUpdate, setConfirmProfileUpdate] = useState(false);
  const [renewalProvider, setRenewalProvider] = useState(''); // State for provider input in prompt
  const [renewalDate, setRenewalDate] = useState(null);
  const [renewalCalendarOpen, setRenewalCalendarOpen] = useState(false); // State for renewal calendar popover
  // Receipt upload state
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [showSmartScanner, setShowSmartScanner] = useState(false);
  const [ocrConfirmation, setOcrConfirmation] = useState(null); // New state for OCR confirmation
    const fileInputRef = useRef(null);

  // Input focus tracking
  const formRef = useRef({
    inputs: {},
    focused: null
  });
  
  // Check if the device is mobile and has a camera
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const hasCamera = navigator.mediaDevices && navigator.mediaDevices.getUserMedia;

  // Add validation function
  const validateForm = () => {
    const newErrors = {};
    if (!newExpense.date) newErrors.date = "Date is required";
    if (!newExpense.category) newErrors.category = "Category is required";
    if (!newExpense.description.trim()) newErrors.description = "Description is required";
    if (!newExpense.amount || isNaN(parseFloat(newExpense.amount)) || parseFloat(newExpense.amount) <= 0) {
      newErrors.amount = "Amount must be a positive number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Load existing expense data if editing
  useEffect(() => {
    if (expenseToEdit) {
      setNewExpense({
        date: expenseToEdit.date ? new Date(expenseToEdit.date) : new Date(),
        category: expenseToEdit.category || '',
        description: expenseToEdit.description || '',
        amount: expenseToEdit.amount || '',
        mileage: expenseToEdit.mileage || '',
        mileageRate: expenseToEdit.mileageRate || '45',
        // Add other fields as needed
      });
      
      // Add receipt handling for existing expenses
      if (expenseToEdit.receiptUrl) {
        setReceiptFile({
          downloadUrl: expenseToEdit.receiptUrl,
          storagePath: expenseToEdit.receiptStoragePath,
          id: expenseToEdit.receiptId,
          isPdf: expenseToEdit.isPdf || expenseToEdit.receiptUrl.toLowerCase().includes('.pdf'),
          name: expenseToEdit.receiptName || (expenseToEdit.isPdf ? 'receipt.pdf' : 'receipt.jpg'),
          compressedSize: expenseToEdit.receiptSize || 0,
        });
        setReceiptPreview({
          type: (expenseToEdit.isPdf || expenseToEdit.receiptUrl.toLowerCase().includes('.pdf')) ? 'pdf' : 'image',
          url: expenseToEdit.receiptUrl,
          name: expenseToEdit.receiptName || (expenseToEdit.isPdf ? 'receipt.pdf' : 'receipt.jpg'),
          size: expenseToEdit.originalReceiptSize || expenseToEdit.receiptSize || 0,
        });
      } else {
        setReceiptFile(null);
        setReceiptPreview(null);
      }
    } else {
      setReceiptFile(null);
      setReceiptPreview(null);
    }
  }, [expenseToEdit]);

  // Date selection handler - copied from QuickEntryForm
  const handleDateSelect = (date) => {
    console.log("Date selected:", date);
    if (date) {
      // Use the existing handleInputChange
      handleInputChange('date', date); 
      
      // Force close the popover after selection
      setTimeout(() => {
        setDatePickerOpen(false);
      }, 100); // Consistent delay
    }
  };
  
  // Input change handler
  const handleInputChange = (field, value) => {
    setNewExpense(prev => ({ ...prev, [field]: value }));
  };
  
  // Helper function to update profile registration
  const updateRegistrationInProfile = async (uid, type, newExpiryDate, providerInput = null) => {
    if (!uid || !type || !newExpiryDate) return;

    try {
      const userDocRef = doc(db, 'users', uid);
      const updateData = {};

      if (type === 'gdc') {
        updateData.gdcExpiryDate = Timestamp.fromDate(newExpiryDate);
        updateData.gdcProvider = 'General Dental Council'; // Always set GDC provider
        updateData.gdcLastUpdated = Timestamp.now();
      } else if (type === 'insurance') {
        updateData.insuranceExpiryDate = Timestamp.fromDate(newExpiryDate);
        if (providerInput && providerInput.trim()) {
          // Encrypt the insurance provider
          updateData.insuranceProvider = encryptData(providerInput.trim());
        }
        updateData.insuranceLastUpdated = Timestamp.now();
      } else {
        return false; // Unknown type
      }

      await updateDoc(userDocRef, updateData);
      console.log(`Profile updated successfully for ${type}`);

      // Clear relevant localStorage dismissal flags after successful update
      const keysToClear = ['expired', 'urgent', 'soon', 'upcoming'];
      keysToClear.forEach(urgency => {
        localStorage.removeItem(getStorageKey(type, urgency));
      });

      // Optionally return true or some indicator of success
      return true;

    } catch (err) {
      console.error(`Error updating ${type} registration in profile:`, err);
      toast({
        title: "Profile Update Failed",
        description: `Could not update ${type} expiry date in your profile.`,
        variant: "destructive",
      });
      return false; // Indicate failure
    }
  };
  
  // Submit expense
  const submitExpense = async (options = {}) => {
    const { skipRenewalCheck = false } = options;

    if (!auth.currentUser) {
      setError('You must be logged in to save expenses');
      return;
    }
    
    setError(null);
    setSuccessMessage("");
    
    // --- Renewal Detection Logic ---
    if (!skipRenewalCheck && !showRenewalPrompt) {
      const renewalTypeInfo = detectRenewal(newExpense.category, newExpense.description);

      if (renewalTypeInfo) { 
        const type = renewalTypeInfo.type;
        const suggestedExpiry = addYears(newExpense.date, 1);
        
        let currentExpiry = null;
        let currentProviderForPrompt = '';

        if (type === 'gdc') {
          currentExpiry = gdcExpiryDate?.toDate ? gdcExpiryDate.toDate() : (gdcExpiryDate ? new Date(gdcExpiryDate) : null);
        } else if (type === 'insurance') {
          currentExpiry = insuranceExpiryDate?.toDate ? insuranceExpiryDate.toDate() : (insuranceExpiryDate ? new Date(insuranceExpiryDate) : null);
          currentProviderForPrompt = currentInsuranceProviderProp || '';
        }

        setPotentialRenewalInfo({ type, currentExpiry, suggestedExpiry });
        setRenewalProvider(currentProviderForPrompt); 
        setShowRenewalPrompt(true);
        setConfirmProfileUpdate(false); 

        return;
      }
    }
    // --- End Renewal Detection ---

    // Validate form
    if (!newExpense.date) {
      setError('Please select a date');
      return;
    }
    
    if (!newExpense.category) {
      setError('Please select a category');
      return;
    }
    
    // Validate travel expenses
    if (newExpense.category === 'travel') {
      if (newExpense.travelType === 'mileage') {
        if (!newExpense.mileage || parseFloat(newExpense.mileage) <= 0) {
          setError('Please enter valid mileage');
          return;
        }
      } else if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }
    }
    // Validate non-travel expenses
    else if (!newExpense.amount || parseFloat(newExpense.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    // Format expense for saving
    let expenseToSave = {
      date: newExpense.date,
      category: newExpense.category,
      description: newExpense.description || `${newExpense.category} expense`
    };
    
    // Add travel-specific fields
    if (newExpense.category === 'travel') {
      expenseToSave.travelType = newExpense.travelType;
      
      if (newExpense.travelType === 'mileage') {
        const mileage = parseFloat(newExpense.mileage);
        const mileageRate = parseFloat(newExpense.mileageRate);
        const totalAmount = (mileage * mileageRate) / 100;
        
        expenseToSave.mileage = mileage;
        expenseToSave.mileageRate = mileageRate;
        expenseToSave.totalAmount = totalAmount;
      } else {
        expenseToSave.amount = newExpense.amount;
        expenseToSave.totalAmount = parseFloat(newExpense.amount);
      }
    }
    // Add amount for non-travel expenses
    else {
      expenseToSave.amount = newExpense.amount;
      expenseToSave.totalAmount = parseFloat(newExpense.amount);
    }
    
    // Add receipt data if available
    if (receiptFile && receiptFile.downloadUrl) {
      expenseToSave.receiptUrl = receiptFile.downloadUrl;
      expenseToSave.receiptId = receiptFile.id;
      expenseToSave.receiptStoragePath = receiptFile.storagePath;
      expenseToSave.receiptName = receiptFile.name;
      expenseToSave.receiptSize = receiptFile.compressedSize;
      expenseToSave.originalReceiptSize = receiptFile.originalSize;
      expenseToSave.compressionRatio = receiptFile.compressionRatio;
      expenseToSave.isPdf = receiptFile.isPdf;
      expenseToSave.hasReceipt = true;
    } else {
      expenseToSave.hasReceipt = false;
    }
    
    setLoading(true);
    
    try {
      // Save expense
      const savedExpense = await expenseService.addExpense(userId, expenseToSave);
      let profileUpdateSuccessful = false;
      let profileUpdateAttempted = false;

      // --- Profile Update Logic ---
      if (showRenewalPrompt && confirmProfileUpdate && potentialRenewalInfo) {
        profileUpdateAttempted = true;
        const providerToUpdate = potentialRenewalInfo.type === 'insurance' ? renewalProvider : null;
        profileUpdateSuccessful = await updateRegistrationInProfile(
          userId,
          potentialRenewalInfo.type,
          potentialRenewalInfo.suggestedExpiry,
          providerToUpdate
        );
      }
      // --- End Profile Update ---
      
      // Reset form to default values - IMPROVED RESET
      setNewExpense({
        date: new Date(), // Keep today's date for convenience
        category: '',
        description: '',
        amount: '',
        travelType: 'mileage',
        mileage: '',
        mileageRate: '45'
      });
      
      // Reset receipt state
      setReceiptFile(null);
      setReceiptPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      
      // Reset renewal prompt state
      setShowRenewalPrompt(false);
      setPotentialRenewalInfo(null);
      setConfirmProfileUpdate(false);
      setRenewalProvider('');

      // Show success message and toast
      let toastDescription = "Expense saved successfully";
      if (profileUpdateAttempted) {
          if (profileUpdateSuccessful) {
              toastDescription += ` and ${potentialRenewalInfo.type.toUpperCase()} profile updated.`;
          } else {
              toastDescription += `, but failed to update ${potentialRenewalInfo.type.toUpperCase()} profile.`;
          }
      }
      
      setSuccessMessage(toastDescription);
      toast({
        title: "Success",
        description: toastDescription,
        variant: profileUpdateAttempted && !profileUpdateSuccessful ? "warning" : "success",
      });
      
      // Notify parent
      if (onExpenseSaved) {
        onExpenseSaved(savedExpense);
      }
      
      // Clear success message after delay
      setTimeout(() => {
        setSuccessMessage("");
      }, 4000);

    } catch (error) {
      console.error('Error saving expense:', error);
      setError('Failed to save expense. Please try again.');
      // Reset renewal prompt state on error too
      setShowRenewalPrompt(false);
      setPotentialRenewalInfo(null);
      setConfirmProfileUpdate(false);
      setRenewalProvider('');
    } finally {
      setLoading(false);
    }
  };

  // Maintain focus after state updates - Use useEffect
  useEffect(() => {
    if (
      formRef.current.focused &&
      formRef.current.inputs[formRef.current.focused] &&
      formRef.current.focused !== 'renewalProvider'
    ) {
      formRef.current.inputs[formRef.current.focused].focus();
    }
  }); // Removed dependency array to run on every render, like QuickEntryForm
  

  // Make the description field background always white in light mode
  const descriptionInputStyle = useMemo(() => {
    if (theme === 'light') {
      return {
        backgroundColor: 'white !important',
        color: 'black !important',
        // Force all states to have white background
        '--tw-bg-opacity': '1 !important',
        // Override any focus/hover states
        '--tw-ring-opacity': '0 !important',
        borderColor: 'rgba(147, 51, 234, 0.3) !important' // Light purple border
      };
    } else {
      return {
        backgroundColor: '#1F1F2E !important',
        color: 'white !important'
      };
    }
  }, [theme]);
  
  // Create a ref for the description input
  const descriptionInputRef = useRef(null);

  // Force background color using direct DOM manipulation whenever theme changes
  useEffect(() => {
    const input = descriptionInputRef.current;
    if (input && theme === 'light') {
      // Apply styles directly to the DOM element
      input.style.setProperty('background-color', 'white', 'important');
      input.style.setProperty('color', 'black', 'important');
      
      // Apply styles on focus/blur events to ensure they persist
      const handleFocus = () => {
        input.style.setProperty('background-color', 'white', 'important');
        input.style.setProperty('color', 'black', 'important');
      };
      
      const handleBlur = () => {
        input.style.setProperty('background-color', 'white', 'important');
        input.style.setProperty('color', 'black', 'important');
      };
      
      // Add event listeners
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
      
      // Clean up
      return () => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      };
    }
    
    // For dark mode, ensure it's using the dark theme colors
    if (input && theme === 'dark') {
      input.style.setProperty('background-color', '#1F1F2E', 'important');
      input.style.setProperty('color', 'white', 'important');
    }
  }, [theme]); // Only run when theme changes
  
  useEffect(() => {
    if (showRenewalPrompt && potentialRenewalInfo) {
      console.log('[EFFECT] Renewal prompt shown/potentialInfo changed. Current renewalDate before set:', renewalDate, 'Suggested:', potentialRenewalInfo.suggestedExpiry);
      const newSuggestedDate = potentialRenewalInfo.suggestedExpiry || null;
      setRenewalDate(newSuggestedDate); // This is the primary initialization
      console.log('[EFFECT] Set renewalDate to suggested:', newSuggestedDate);

      if (potentialRenewalInfo.type === 'insurance') {
        setRenewalProvider(currentInsuranceProviderProp ? decryptData(currentInsuranceProviderProp) : '');
      } else {
        setRenewalProvider('');
      }
    }
  }, [showRenewalPrompt, potentialRenewalInfo, currentInsuranceProviderProp]); // Effect for initializing renewal data
  
  const handleConfirmRenewal = async () => {
    console.log('[CONFIRM] handleConfirmRenewal called.');
    console.log('[CONFIRM] Current renewalDate state:', renewalDate);
    console.log('[CONFIRM] potentialRenewalInfo.suggestedExpiry:', potentialRenewalInfo?.suggestedExpiry);
    console.log('[CONFIRM] confirmProfileUpdate checkbox state:', confirmProfileUpdate);

    // Save the expense first, then handle profile update
    setShowRenewalPrompt(false);
    setPotentialRenewalInfo(null);
    setRenewalDate(null);
    setRenewalProvider('');
    setConfirmProfileUpdate(false);
    
    // Proceed to save the expense itself - FIXED: Always save the expense
    await submitExpense({ skipRenewalCheck: true });

    // THEN handle profile update if checkbox was checked
    if (potentialRenewalInfo && renewalDate && confirmProfileUpdate) {
      const success = await updateRegistrationInProfile(
        userId, 
        potentialRenewalInfo.type, 
        renewalDate, 
        potentialRenewalInfo.type === 'insurance' ? renewalProvider : null
      );
      if (success) {
        toast({ title: "Profile Updated", description: `${potentialRenewalInfo.type.toUpperCase()} registration details saved.`, variant: "success" });
      } else {
        toast({ title: "Update Failed", description: `Could not update ${potentialRenewalInfo.type.toUpperCase()} registration.`, variant: "destructive" });
      }
    }
  };

  // When showing the renewal prompt, always pre-fill the provider and expiry date
  useEffect(() => {
    if (showRenewalPrompt && potentialRenewalInfo) {
      // Pre-fill provider for insurance
      if (potentialRenewalInfo.type === 'insurance') {
        setRenewalProvider(currentInsuranceProviderProp ? decryptData(currentInsuranceProviderProp) : '');
      }
      // Pre-fill expiry date with suggested expiry
      setRenewalDate(potentialRenewalInfo.suggestedExpiry || null);
    }
    // eslint-disable-next-line
  }, [showRenewalPrompt, potentialRenewalInfo, currentInsuranceProviderProp]);
  // Receipt upload functions
  const processReceiptImage = async (file) => {
    setReceiptUploading(true);
    setErrors(prev => ({ ...prev, receipt: null }));

    try {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        throw new Error('Please select an image or PDF file');
      }
      if (!auth.currentUser) throw new Error('User not authenticated.');

      const isPdf = file.type === 'application/pdf';
      let previewData;

      if (isPdf) {
        previewData = { type: 'pdf', name: file.name, size: file.size };
      } else {
        const imagePreviewUrl = URL.createObjectURL(file);
        previewData = { type: 'image', url: imagePreviewUrl, name: file.name, size: file.size };
      }
      setReceiptPreview(previewData);

      const fileToUpload = isPdf ? file : await compressReceiptImage(file);
      
      const fileExtension = isPdf ? 'pdf' : 'jpg';
      const receiptId = `receipt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const storagePath = `users/${auth.currentUser.uid}/receipts/${receiptId}.${fileExtension}`;
      const storage = getStorage();
      const storageRef = ref(storage, storagePath);
      
      const metadata = {
        contentType: file.type,
        customMetadata: {
          originalSize: file.size.toString(),
          compressedSize: fileToUpload.size.toString(),
          uploadedBy: auth.currentUser.uid,
          isPdf: isPdf.toString(),
          originalName: file.name,
        }
      };

      await uploadBytes(storageRef, fileToUpload, metadata);
      const downloadUrl = await getDownloadURL(storageRef);

      setReceiptFile({
        id: receiptId,
        name: file.name,
        type: file.type,
        downloadUrl: downloadUrl,
        storagePath: storagePath,
        originalSize: file.size,
        compressedSize: fileToUpload.size,
        compressionRatio: isPdf ? '0' : ((file.size - fileToUpload.size) / file.size * 100).toFixed(1),
        isPdf: isPdf,
      });

      // Add OCR processing for images (not PDFs)
      let ocrResults = null;
      if (!isPdf) {
        try {
          ocrResults = await extractReceiptText(file);
          
          // Check if OCR returned an error
          if (ocrResults.error) {
            throw new Error(ocrResults.error);
          }
          
          // Show OCR results to user via confirmation dialog
          if (ocrResults.amount || ocrResults.vendor) {
            setOcrConfirmation({
              amount: ocrResults.amount ? {
                value: ocrResults.amount.toFixed(2),
                confidence: ocrResults.allAmounts?.[0]?.confidence || ocrResults.confidence || 0
              } : null,
              vendor: ocrResults.vendor ? {
                value: ocrResults.vendor,
                confidence: ocrResults.confidence || 0
              } : null,
              onApprove: (approvedData) => {
                if (approvedData.amount) {
                  handleInputChange('amount', approvedData.amount);
                }
                if (approvedData.vendor) {
                  handleInputChange('description', approvedData.vendor);
                }
                toast({
                  title: "OCR Values Applied",
                  description: `Updated ${Object.keys(approvedData).length} field(s)`,
                  variant: "success",
                  duration: 3000,
                });
                setOcrConfirmation(null);
              },
              onCancel: () => setOcrConfirmation(null)
            });
          }
          
        } catch (ocrError) {
          console.warn('OCR processing failed:', ocrError);
          toast({
            title: "OCR processing failed",
            description: "Receipt uploaded but text detection failed.",
            variant: "warning",
            duration: 4000,
          });
        }
      }

      toast({
        title: `${isPdf ? 'PDF' : 'Receipt'} uploaded`,
        description: `File ready to be saved with expense.`,
        variant: "success",
      });

    } catch (error) {
      console.error('Error processing receipt:', error);
      setErrors(prev => ({ ...prev, receipt: `Upload failed: ${error.message}` }));
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      setReceiptPreview(null);
      setReceiptFile(null);
    } finally {
      setReceiptUploading(false);
    }
  };

  const compressReceiptImage = async (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        const maxSize = 1000;
        let { width, height } = img;
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
        }, 'image/jpeg', 0.7);
      };
      img.src = URL.createObjectURL(file);
    });
  };
  const startSmartScanner = () => {
    setShowSmartScanner(true);
  };

  const handleSmartCapture = async (file) => {
    await processReceiptImage(file);
    setShowSmartScanner(false);
  };

  const closeSmartScanner = () => {
    setShowSmartScanner(false);
  };

  const removeReceipt = () => {
    setReceiptPreview(null);
    setReceiptFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Submit handler - Use submitExpense directly for renewal flow or handleConfirmRenewal for renewal confirmation
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (showRenewalPrompt) {
      await handleConfirmRenewal();
    } else {
      await submitExpense();
    }
  };

  return (
    <div className={cn(
      "rounded-lg shadow-md p-6",
      theme === 'light' ? "bg-white" : "bg-[#2A2A3E]"
    )}>
      <h2 className={cn(
        "text-lg font-semibold mb-4",
        theme === 'light' ? "text-gray-900" : "text-white"
      )}>
        {expenseToEdit ? 'Edit Expense' : 'Add Expense'}
      </h2>
      
      {/* Success message alert - ADD THIS */}
      {successMessage && (
        <Alert className="mb-4 bg-green-900/20 border-green-500/50">
          <Check className="h-4 w-4 mr-2 text-green-500" />
          <AlertDescription className="text-green-500">{successMessage}</AlertDescription>
        </Alert>
      )}
      
      {/* Error alert - ADD THIS */}
      {error && (
        <Alert variant="destructive" className="mb-4 bg-red-900/20 border-red-500/50">
          <AlertTriangle className="h-4 w-4 mr-2" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleFormSubmit} className="space-y-4">
        {/* Date and category fields - existing code */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Date picker - Apply conditional styles */}
          <div>
            <Label 
              htmlFor="date" 
              className={cn(
                "mb-1 block",
                theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
              )}
            >
              Date
            </Label>
            <div className="relative">
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal border-app-accent-purple", // Consistent border
                      // Conditional styles matching QuickEntryForm
                      theme === 'light'
                        ? "bg-white text-black hover:bg-white hover:text-black focus:bg-white active:bg-gray-100" 
                        : "bg-[#1F1F2E] text-white hover:bg-[#2a2a3e] focus:bg-[#1F1F2E] active:bg-[#2a2a3e]", 
                      !newExpense.date && (theme === 'light' ? "text-gray-500" : "text-gray-400")
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newExpense.date ? format(newExpense.date, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                {/* PopoverContent should adapt via global styles or explicit conditional styles */}
                <PopoverContent 
                  className="w-auto p-0 border-app-accent-purple" // Consistent border
                  // Removed hardcoded background, rely on global or add conditional
                  // key={`date-popover-content-${datePickerOpen}`} // Key might not be needed
                >
                  <SimpleCalendar
                    value={newExpense.date}
                    onChange={handleDateSelect}
                    // Rely on SimpleCalendar's internal/global theme styling
                    className="border-none" 
                    onClose={() => setDatePickerOpen(false)} // Add onClose prop
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          {/* Category selection - Apply conditional styles */}
          <div>
            <Label 
              htmlFor="category" 
              className={cn(
                "mb-1 block",
                theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
              )}
            >
              Category
            </Label>
            <Select 
              value={newExpense.category} 
              onValueChange={(value) => handleInputChange('category', value)}
            >
              <SelectTrigger 
                id="category" 
                // Rely on global input/select styles or add conditional
                className={cn(
                  "border-app-accent-purple", // Consistent border
                   theme === 'light' 
                     ? "bg-white text-black" 
                     : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
                )}
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              {/* SelectContent should adapt via global styles or explicit conditional styles */}
              <SelectContent className="border-app-accent-purple"> 
                {categories.map(category => (
                  <SelectItem 
                    key={category.value} 
                    value={category.value}
                    // Rely on global item styles or add conditional
                    className={cn(
                      theme === 'light' 
                        ? "text-black focus:bg-accent focus:text-accent-foreground" // Standard light focus
                        : "text-white focus:bg-accent focus:text-accent-foreground" // Standard dark focus
                    )}
                  >
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Travel specific fields - Apply conditional styles */}
        {newExpense.category === 'travel' && (
          <div className="space-y-4 mb-4">
            <div>
              <Label 
                htmlFor="travelType" 
                className={cn(
                  "mb-1 block",
                  theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
                )}
              >
                Travel Type
              </Label>
              <Select 
                value={newExpense.travelType} 
                onValueChange={(value) => handleInputChange('travelType', value)}
              >
                <SelectTrigger 
                  id="travelType" 
                  // Rely on global input/select styles or add conditional
                  className={cn(
                    "border-app-accent-purple", // Consistent border
                    theme === 'light' 
                      ? "bg-white text-black" 
                      : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
                  )}
                >
                  <SelectValue placeholder="Select travel type" />
                </SelectTrigger>
                {/* SelectContent should adapt via global styles or explicit conditional styles */}
                <SelectContent className="border-app-accent-purple">
                  <SelectItem 
                    value="mileage" 
                    className={cn(
                      theme === 'light' 
                        ? "text-black focus:bg-accent focus:text-accent-foreground" 
                        : "text-white focus:bg-accent focus:text-accent-foreground"
                    )}
                  >
                    Mileage
                  </SelectItem>
                  <SelectItem 
                    value="publicTransport" 
                    className={cn(
                      theme === 'light' 
                        ? "text-black focus:bg-accent focus:text-accent-foreground" 
                        : "text-white focus:bg-accent focus:text-accent-foreground"
                    )}
                  >
                    Public Transport
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {newExpense.travelType === 'mileage' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label 
                    htmlFor="mileage" 
                    className={cn(
                      "mb-1 block",
                      theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
                    )}
                  >
                    Mileage
                  </Label>
                  <Input 
                    id="mileage"
                    type="number" 
                    value={newExpense.mileage} 
                    onChange={(e) => {
                      formRef.current.focused = 'mileage';
                      handleInputChange('mileage', e.target.value);
                    }}
                    // Rely on global input styles or add conditional
                    className={cn(
                      "border-app-accent-purple", // Consistent border
                      theme === 'light' 
                        ? "bg-white text-black" 
                        : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
                    )}
                    ref={(el) => {
                      if (el) formRef.current.inputs['mileage'] = el;
                    }}
                    placeholder="Enter miles"
                  />
                </div>
                <div>
                  <Label 
                    htmlFor="mileageRate" 
                    className={cn(
                      "mb-1 block",
                      theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
                    )}
                  >
                    Rate (pence per mile)
                  </Label>
                  <Input 
                    id="mileageRate"
                    type="number" 
                    value={newExpense.mileageRate} 
                    onChange={(e) => {
                      formRef.current.focused = 'mileageRate';
                      handleInputChange('mileageRate', e.target.value);
                    }}
                    // Rely on global input styles or add conditional
                    className={cn(
                      "border-app-accent-purple", // Consistent border
                      theme === 'light' 
                        ? "bg-white text-black" 
                        : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
                    )}
                    ref={(el) => {
                      if (el) formRef.current.inputs['mileageRate'] = el;
                    }}
                    placeholder="Rate in pence"
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label 
                  htmlFor="amount" 
                  className={cn(
                    "mb-1 block",
                    theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
                  )}
                >
                  Amount (£)
                </Label>
                <Input 
                  id="amount"
                  type="number" 
                  value={newExpense.amount} 
                  onChange={(e) => {
                    formRef.current.focused = 'amount';
                    handleInputChange('amount', e.target.value);
                  }}
                  // Rely on global input styles or add conditional
                  className={cn(
                    "border-app-accent-purple", // Consistent border
                    theme === 'light' 
                      ? "bg-white text-black" 
                      : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
                  )}
                  ref={(el) => {
                    if (el) formRef.current.inputs['amount'] = el;
                  }}
                  placeholder="Enter amount in pounds"
                />
              </div>
            )}
          </div>
        )}
        
        {/* Amount field for non-travel expenses - Apply conditional styles */}
        {newExpense.category && newExpense.category !== 'travel' && (
          <div className="mb-4">
            <Label 
              htmlFor="amount" 
              className={cn(
                "mb-1 block",
                theme === 'light' ? 'text-black' : 'text-primary' // Use theme for label color
              )}
            >
              Amount (£)
            </Label>
            <Input 
              id="amount"
              type="number" 
              value={newExpense.amount} 
              onChange={(e) => {
                formRef.current.focused = 'amount';
                handleInputChange('amount', e.target.value);
              }}
              // Rely on global input styles or add conditional
              className={cn(
                "border-app-accent-purple", // Consistent border
                theme === 'light' 
                  ? "bg-white text-black" 
                  : "bg-[#1F1F2E] text-white" // Keep dark explicit for now if no global
              )}
              ref={(el) => {
                if (el) formRef.current.inputs['amount'] = el;
              }}
              placeholder="Enter amount in pounds"
            />
          </div>
        )}
        
        {/* Description field - Apply ultra aggressive styling fix */}
        <div className="mb-4">
          <Label 
            htmlFor="description" 
            className={cn(
              "mb-1 block",
              theme === 'light' ? 'text-black' : 'text-primary'
            )}
          >
            Description (optional)
          </Label>
          <Input 
            id="description"
            value={newExpense.description} 
            onChange={(e) => {
              formRef.current.focused = 'description';
              handleInputChange('description', e.target.value);
            }}
            // Override component styles completely with !important flags
            style={theme === 'light' 
              ? { 
                  backgroundColor: 'white !important', 
                  color: 'black !important',
                  // Force all states to white in light mode
                  '--tw-bg-opacity': '1 !important',
                  border: '1px solid rgba(147, 51, 234, 0.3) !important'
                } 
              : { 
                  backgroundColor: '#1F1F2E !important', 
                  color: 'white !important' 
                }
            }
            // Remove background-related classes that might be causing conflicts
            className="border-app-accent-purple"
            ref={(el) => {
              // Save references for both the form's input tracking and our direct DOM manipulation
              if (el) {
                formRef.current.inputs['description'] = el;
                descriptionInputRef.current = el;
              }
            }}
            placeholder="Enter a brief description"
          />
        </div>
        
        {/* --- Renewal Prompt UI --- */}
        {showRenewalPrompt && potentialRenewalInfo && (
          <div className={cn(
            "my-4 p-3 border rounded-md",
            theme === 'light'
              ? 'bg-blue-50 border-blue-300'
              : 'bg-blue-900/30 border-blue-500/50'
          )}>
            <h4 className={cn(
              "font-semibold mb-2",
               theme === 'light' ? 'text-blue-800' : 'text-blue-200'
            )}>
              Update Registration?
            </h4>
            <p className={cn(
              "text-sm mb-1",
              theme === 'light' ? 'text-blue-700' : 'text-blue-300'
            )}>
              This looks like a {potentialRenewalInfo.type === 'gdc' ? 'GDC' : 'Insurance'} renewal.
            </p>
            <p className={cn(
              "text-sm mb-1",
               theme === 'light' ? 'text-blue-700' : 'text-blue-300'
            )}>
              Current expiry: {
                potentialRenewalInfo.type === 'gdc'
                  ? (gdcExpiryDate ? format(gdcExpiryDate.toDate ? gdcExpiryDate.toDate() : new Date(gdcExpiryDate), 'dd MMM yyyy') : 'Not set')
                  : (insuranceExpiryDate ? format(insuranceExpiryDate.toDate ? insuranceExpiryDate.toDate() : new Date(insuranceExpiryDate), 'dd MMM yyyy') : 'Not set')
              }
            </p>
            {potentialRenewalInfo.type === 'insurance' && (
              <p className={cn(
                "text-sm mb-1",
                theme === 'light' ? 'text-blue-700' : 'text-blue-300'
              )}>
                Current provider: {currentInsuranceProviderProp ? decryptData(currentInsuranceProviderProp) : 'Not set'}
              </p>
            )}
            <p className={cn(
              "text-sm mb-3",
               theme === 'light' ? 'text-blue-700' : 'text-blue-300'
            )}>
              Suggested new expiry: {format(potentialRenewalInfo.suggestedExpiry, 'dd MMM yyyy')}
            </p>
            {/* Insurance Provider input always pre-filled with current provider, editable */}
            {potentialRenewalInfo.type === 'insurance' && (
              <div className="mb-3">
                <Label 
                  htmlFor="renewalProvider" 
                  className={cn(
                    "text-sm mb-1 block",
                    theme === 'light' ? 'text-blue-800' : 'text-blue-200'
                  )}
                >
                  Insurance Provider
                </Label>
                <Input
                  id="renewalProvider"
                  value={renewalProvider !== undefined && renewalProvider !== ''
                    ? renewalProvider
                    : (currentInsuranceProviderProp ? decryptData(currentInsuranceProviderProp) : '')}
                  onChange={(e) => {
                    setRenewalProvider(e.target.value);
                    // Clear the main form's focus tracking to prevent focus jumps
                    if (formRef.current) {
                      formRef.current.focused = null;
                    }
                  }}
                  placeholder="Enter provider name"
                  className={cn(
                    "text-sm",
                    theme === 'light' && "text-black placeholder:text-gray-500"
                  )}
                />
              </div>
            )}
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="renewalDate" className={cn(
                  "mb-1 block",
                  theme === 'light' ? 'text-blue-800' : 'text-blue-200'
                )}>
                  New Expiry Date
                </Label>
                <Popover open={renewalCalendarOpen} onOpenChange={setRenewalCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        theme === 'light'
                          ? "bg-white text-black border-blue-300"
                          : "bg-[#1F1F2E] text-white border-blue-500",
                        !renewalDate && "text-muted-foreground"
                      )}
                      style={theme === 'light'
                        ? { backgroundColor: 'white', color: 'black', borderColor: '#93c5fd' }
                        : { backgroundColor: '#1F1F2E', color: 'white', borderColor: '#3b82f6' }
                      }
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {/* Display renewalDate if set, otherwise the suggested one for initial view */}
                      {(renewalDate || potentialRenewalInfo.suggestedExpiry) ? format((renewalDate || potentialRenewalInfo.suggestedExpiry), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 app-card-bg border-border" align="start">
                    <SimpleCalendar
                      mode="single"
                      value={renewalDate || potentialRenewalInfo.suggestedExpiry} // Controlled component value
                      onChange={(day) => { // Use onSelect for DayPicker
                          console.log('Calendar onChange day:', day);
                          if (day !== undefined) {
                            setRenewalDate(day);
                            setRenewalCalendarOpen(false);
                          }
                      }}
                      onSelect={(day) => { // Use onSelect for DayPicker
                          console.log('Calendar onSelect day:', day);
                          if (day !== undefined) {
                            setRenewalDate(day);
                            setRenewalCalendarOpen(false);
                          }
                      }}
                      initialFocus
                      defaultMonth={renewalDate || potentialRenewalInfo?.suggestedExpiry || new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="confirmUpdate"
                checked={confirmProfileUpdate}
                onCheckedChange={setConfirmProfileUpdate}
                className={cn(
                  "border-2",
                  theme === 'light' 
                    ? "border-blue-600 hover:border-blue-700 data-[state=checked]:bg-blue-600"
                    : "border-blue-300 hover:border-blue-200 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white",
                  "focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                )}
              />
              <Label 
                htmlFor="confirmUpdate" 
                className={cn(
                  "text-sm font-medium cursor-pointer",
                   theme === 'light' ? 'text-purple-600 font-semibold' : 'text-purple-300 font-semibold' // More prominent style
                )}
              >
                Yes, update my profile expiry date.
              </Label>
            </div>
          </div>
        )}
        {/* --- End Renewal Prompt UI --- */}
        
        {/* Receipt upload section - Fixed dark mode styling */}
        <div>
          <Label className={cn(
            "block text-sm font-medium mb-1",
            theme === 'light' ? 'text-gray-700' : 'text-gray-300'
          )}>
            Receipt (Optional)
          </Label>
          {receiptPreview ? (
            <div className="flex items-start gap-3 mt-2">
              <div className="relative">
                {receiptPreview.type === 'pdf' ? (
                  <div className={cn(
                    "w-16 h-20 flex flex-col items-center justify-center border rounded",
                    theme === 'light' 
                      ? "bg-gray-100 text-gray-600 border-gray-300" 
                      : "bg-gray-700 text-gray-300 border-gray-600"
                  )}>
                    <FileText className="h-6 w-6 mb-1" />
                    <span className="text-xs text-center leading-tight">{receiptPreview.name.substring(0, 10)}...</span>
                  </div>
                ) : (
                  <img 
                    src={receiptPreview.url} 
                    alt="Receipt preview" 
                    className={cn(
                      "w-16 h-20 object-cover rounded border",
                      theme === 'light' ? "border-gray-300" : "border-gray-600",
                      receiptUploading && "opacity-70"
                    )}
                  />
                )}                <Button 
                  type="button" 
                  size="sm" 
                  variant="ghost" 
                  className="absolute -top-2 -right-2 h-5 w-5 p-0 bg-black/60 hover:bg-black/80 rounded-full text-white"
                  onClick={removeReceipt}
                  disabled={receiptUploading}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className={cn(
                "text-xs pt-1",
                theme === 'light' ? 'text-gray-500' : 'text-gray-400'
              )}>
                {receiptFile?.compressedSize ? (receiptFile.compressedSize / 1024).toFixed(0) : (receiptPreview.size / 1024).toFixed(0)}KB
                {receiptUploading && (
                  <div className="flex items-center mt-1">
                    <LoadingSpinner size="small" />
                    <span className="ml-1">Processing...</span>
                  </div>
                )}
              </div>
            </div>
          ) : receiptUploading ? (
            <div className={cn(
              "flex items-center justify-center h-12 border rounded-md mt-2",
              theme === 'light' 
                ? 'bg-gray-50 border-gray-200 text-gray-500' 
                : 'bg-gray-700/30 border-gray-600 text-gray-400'
            )}>
              <LoadingSpinner size="small" />
              <span className="ml-2 text-sm">Processing...</span>
            </div>
          ) : (            <div className="flex gap-2 mt-2">
              {hasCamera && isMobile && (
                <div className="flex flex-col">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={startSmartScanner} 
                    disabled={receiptUploading}
                    className={cn(
                      "flex items-center gap-1 text-xs px-3 py-1.5 border",
                      theme === 'light' 
                        ? 'bg-white border-purple-300 text-purple-700 hover:bg-purple-50' 
                        : 'bg-gray-800 border-purple-500/50 text-purple-300 hover:bg-purple-900/30'
                    )}
                  >
                    <Clock className="h-3 w-3" />
                    Smart Capture
                  </Button>
                  <div className={cn(
                    "text-xs italic mt-1 ml-1",
                    theme === 'light' ? 'text-purple-500/70' : 'text-purple-400/70'
                  )}>
                    AI-powered (Beta)
                  </div>
                </div>
              )}
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()} 
                disabled={receiptUploading}
                className={cn(
                  "flex items-center gap-1 text-xs px-3 py-1.5 border",
                  theme === 'light' 
                    ? 'bg-white border-purple-300 text-purple-700 hover:bg-purple-50' 
                    : 'bg-gray-800 border-purple-500/50 text-purple-300 hover:bg-purple-900/30'
                )}
              >
                <Upload className="h-3 w-3" /> Upload
              </Button>
              <input 
                ref={fileInputRef} 
                type="file" 
                accept="image/*,application/pdf" 
                className="hidden" 
                onChange={(e) => e.target.files?.[0] && processReceiptImage(e.target.files[0])} 
              />
            </div>
          )}
          {errors.receipt && <p className="text-red-500 text-xs mt-1">{errors.receipt}</p>}
        </div>        
        {/* Save Button - Disable during receipt upload */}
        <Button 
          type="submit"
          className="w-full bg-purple-600 hover:bg-purple-700 text-white" 
          disabled={loading || receiptUploading}
        >
          {loading ? (
            <LoadingSpinner size="small" />
          ) : receiptUploading ? (
            <>
              <LoadingSpinner size="small" />
              <span className="ml-2">Processing Receipt...</span>
            </>
          ) : (
            showRenewalPrompt ? "Confirm & Save Expense" : "Save Expense"
          )}
        </Button>
      </form>

      {/* OCR Confirmation Dialog */}
      {ocrConfirmation && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={cn(
            "bg-white dark:bg-[#2A2A3E] rounded-lg border shadow-lg max-w-md w-full",
            "border-purple-200 dark:border-purple-500/30"
          )}>
            <div className="p-4 border-b border-purple-200 dark:border-purple-500/30">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Receipt Scan Results
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Review and approve the detected values
              </p>
            </div>
            
            <div className="p-4 space-y-4">
              {ocrConfirmation.amount && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Amount: £{ocrConfirmation.amount.value}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {ocrConfirmation.amount.confidence.toFixed(0)}% confidence
                    </div>
                  </div>
                  <input 
                    type="checkbox"
                    id="approve-amount"
                    defaultChecked={true}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-purple-300 rounded"
                  />
                </div>
              )}
              
              {ocrConfirmation.vendor && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Vendor: {ocrConfirmation.vendor.value}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {ocrConfirmation.vendor.confidence.toFixed(0)}% confidence
                    </div>
                  </div>
                  <input 
                    type="checkbox"
                    id="approve-vendor"
                    defaultChecked={ocrConfirmation.vendor.confidence > 70}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-purple-300 rounded"
                  />
                </div>
              )}
              
              {!ocrConfirmation.amount && !ocrConfirmation.vendor && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  No values were detected from this receipt
                </div>
              )}
            </div>              <div className="p-4 border-t border-purple-200 dark:border-purple-500/30 flex gap-2">
                <Button
                  variant="outline"
                  onClick={ocrConfirmation.onCancel}
                  className={cn(
                    "flex-1 border-purple-300",
                    theme === 'light' 
                      ? 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300' 
                      : 'bg-gray-700 text-white hover:bg-gray-600 border-gray-600'
                  )}
                >
                  Cancel
                </Button>
              <Button
                onClick={() => {
                  const approvedData = {};
                  
                  if (ocrConfirmation.amount) {
                    const amountCheckbox = document.getElementById('approve-amount');
                    if (amountCheckbox?.checked) {
                      approvedData.amount = ocrConfirmation.amount.value;
                    }
                  }
                  
                  if (ocrConfirmation.vendor) {
                    const vendorCheckbox = document.getElementById('approve-vendor');
                    if (vendorCheckbox?.checked) {
                      approvedData.vendor = ocrConfirmation.vendor.value;
                    }
                  }
                  
                  ocrConfirmation.onApprove(approvedData);
                }}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Apply Selected
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Smart scanner component */}
      <SmartReceiptScanner
        isOpen={showSmartScanner}
        onCaptureComplete={handleSmartCapture}
        onClose={closeSmartScanner}
      />    </div>
  );
};

export default StandardExpenseForm;
